import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Avatar, message, Modal } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  LogoutOutlined,
  ExclamationCircleOutlined,
  IdcardOutlined,
  ProjectOutlined,
  QrcodeOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { authApi } from '@/services/api';
import './styles/AdminLayout.css';
import logo from '@/assets/img/logo.png?url';

const { Header, Sider, Content } = Layout;

const WORK_ORDER_ROLES = ['technician', 'software_engineer', 'hardware_engineer', 'business_manager', 'project_manager'];

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = authApi.getCurrentUser();
  const isWorkOrderUser = !!user?.role && WORK_ORDER_ROLES.includes(user.role);

  useEffect(() => {
    if (
      isWorkOrderUser &&
      !location.pathname.includes('/aftersales') &&
      !location.pathname.includes('/project-orders') &&
      !location.pathname.includes('/profile')
    ) {
      navigate('/admin/aftersales', { replace: true });
    }
  }, [isWorkOrderUser, location.pathname, navigate]);

  const adminMenuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: '企安工单中台',
      onClick: () => navigate('/admin/dashboard'),
    },
    {
      key: 'employee-serials',
      icon: <IdcardOutlined />,
      label: '权限管理',
      onClick: () => navigate('/admin/employee-serials'),
    },
    {
      key: 'product-traces',
      icon: <QrcodeOutlined />,
      label: '产品溯源',
      onClick: () => navigate('/admin/product-traces'),
    },
    {
      key: 'project-orders',
      icon: <ProjectOutlined />,
      label: '项目工单',
      onClick: () => navigate('/admin/project-orders'),
    },
    {
      key: 'aftersales',
      icon: <ToolOutlined />,
      label: '售后工单',
      onClick: () => navigate('/admin/aftersales'),
    },
  ];
  const technicianMenuItems = [
    {
      key: 'project-orders',
      icon: <ProjectOutlined />,
      label: '项目工单',
      onClick: () => navigate('/admin/project-orders'),
    },
    {
      key: 'aftersales',
      icon: <ToolOutlined />,
      label: '售后工单',
      onClick: () => navigate('/admin/aftersales'),
    },
  ];
  const menuItems = isWorkOrderUser ? technicianMenuItems : adminMenuItems;

  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出',
      icon: <ExclamationCircleOutlined />,
      content: '您确定要退出登录吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await authApi.logout();
          message.success('已退出登录');
          navigate('/login');
        } catch (error) {
          message.error('退出失败');
        }
      },
    });
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '用户资料',
      onClick: () => navigate('/admin/profile'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
      danger: true,
    },
  ];

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.includes('/dashboard')) return 'dashboard';
    if (path.includes('/employee-serials')) return 'employee-serials';
    if (path.includes('/product-traces')) return 'product-traces';
    if (path.includes('/project-orders')) return 'project-orders';
    if (path.includes('/aftersales')) return 'aftersales';
    if (path.includes('/profile')) return 'profile';
    return 'dashboard';
  };

  const getTitle = () => {
    const path = location.pathname;
    if (path.includes('/dashboard')) return '企安工单中台';
    if (path.includes('/employee-serials')) return '权限管理';
    if (path.includes('/product-traces')) return '产品溯源';
    if (path.includes('/project-orders')) return '项目工单';
    if (path.includes('/aftersales')) return '售后工单';
    if (path.includes('/profile')) return '用户资料';
    return '企安工单中台';
  };

  return (
    <Layout className="admin-layout">
      <Sider
        className="admin-sider"
        width={256}
      >
        <div className="logo">
          <img src={logo} alt="Logo" style={{ height: '40px' }} />
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
          className="admin-menu"
        />
      </Sider>
      
      <Layout>
        <Header className="admin-header">
          <div className="header-title">
            {getTitle()}
          </div>
          <div className="header-right">
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="user-info">
                <Avatar icon={<UserOutlined />} />
                <span className="username">{user?.name || user?.username}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        
        <Content className="admin-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}

export default AdminLayout;
